import React, { memo } from "react";
import { Handle, Position } from "react-flow-renderer";
import { Typography, Chip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { ContentCopy, Delete, ArrowForwardIos, Storage } from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";

// Cores para cada tipo de banco
const dbColors = {
  postgres: "#336791",
  mysql: "#4479A1",
  sqlite: "#003B57",
  mssql: "#CC2927",
};

function SqlNode({ data, isConnectable, id }) {
  const theme = useTheme();
  const storageItems = useNodeStorage();

  const queryName = data?.sqlConfig?.queryName || "Consulta SQL";
  const dbType = data?.sqlConfig?.dbType || "postgres";
  const queryType = data?.sqlConfig?.queryType || "select";
  const borderColor = dbColors[dbType] || "#336791";

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.paper,
        padding: "12px",
        borderRadius: "8px",
        boxShadow: theme.shadows[3],
        border: `2px solid ${borderColor}`,
        minWidth: "180px",
        maxWidth: "220px",
      }}
    >
      {/* Handle de entrada */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          top: "50%",
          left: "-14px",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, ${borderColor} 0%, ${borderColor}dd 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
                transform: "rotate(180deg)",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Botões de ação */}
      <div
        style={{
          display: "flex",
          position: "absolute",
          right: 5,
          top: 5,
          cursor: "pointer",
          gap: 6,
        }}
      >
        <ContentCopy
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("duplicate");
          }}
          style={{
            width: "14px",
            height: "14px",
            color: theme.palette.primary.main,
          }}
        />
        <Delete
          onClick={() => {
            storageItems.setNodesStorage(id);
            storageItems.setAct("delete");
          }}
          style={{
            width: "14px",
            height: "14px",
            color: theme.palette.error.main,
          }}
        />
      </div>

      {/* Header com ícone e nome */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <Storage
          style={{
            width: "22px",
            height: "22px",
            color: borderColor,
          }}
        />
        <Typography
          variant="subtitle2"
          style={{
            fontWeight: "bold",
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {queryName}
        </Typography>
      </div>

      {/* Chips de status */}
      <div
        style={{
          display: "flex",
          gap: "4px",
          flexWrap: "wrap",
        }}
      >
        <Chip
          label={dbType.toUpperCase()}
          size="small"
          style={{
            backgroundColor: borderColor,
            color: "#fff",
            fontSize: "10px",
            height: "20px",
          }}
        />
        <Chip
          label={queryType.toUpperCase()}
          size="small"
          variant="outlined"
          style={{
            fontSize: "10px",
            height: "20px",
            borderColor: borderColor,
            color: borderColor,
          }}
        />
      </div>

      {/* Handle de saída - Sucesso */}
      <Handle
        type="source"
        position={Position.Right}
        id="success"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          top: "35%",
          right: "-14px",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #22c55e 0%, #16a34a 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>

      {/* Handle de saída - Erro */}
      <Handle
        type="source"
        position={Position.Right}
        id="error"
        style={{
          background: "transparent",
          width: "28px",
          height: "28px",
          top: "65%",
          right: "-14px",
          cursor: "crosshair",
          border: "none",
        }}
        isConnectable={isConnectable}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: `linear-gradient(145deg, #ef4444 0%, #dc2626 100%)`,
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              boxShadow: theme.shadows[2],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
            }}
          >
            <ArrowForwardIos
              style={{
                color: "#fff",
                width: "12px",
                height: "12px",
              }}
            />
          </div>
        </div>
      </Handle>
    </div>
  );
}

export default memo(SqlNode);
